import { API, CurrencyType, Endpoints, Nullable, Project } from '@brg/api';
import { ButtonVariant, useToasts } from '@brg/ui';
import { ProjectDelegate } from 'project-delegate';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ProjectCardNew from 'shared/components/projectCard/projectCardNew';
import { ProjectsGridNew } from 'shared/components/grids';
import { computeProject } from 'shared/helpers/types/computed/ComputedProject';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { useModal } from '../../../core';
import { EntityPaymentTypes } from '../../features/QueuePaymenrt/constants/payment';
import { FlowType } from '../../features/QueuePaymenrt/constants/flowType';
import { clearOrderFields, selectorOrder, setImmediatePurchaseProps } from '../../features/QueuePaymenrt/slices/OrderSlice';
import { QUERY_LEGAL_CONTEST_PROJECT_THEN_FORM } from '_competition/const';
import {
  ActionsRow,
  Container,
  ContentDesc,
  ContentDescSecondary,
  ContentTitle,
  ProjectsSection,
  SectionHeading,
  StyledActionButton,
  Title,
  Wrapper,
} from './PaidWithFormParticipateModal.styles';


export type PaidWithFormParticipateModalProps = {
  onClose?: () => void;
  competitionId?: string;
  participationProjects?: Project[];
  pendingProjects?: Project[];
  rejectedProjects?: Project[];
};
interface CompetitionCardNameItem {
  lang?: string;
  name?: string;
}

const isEditProjectPath = (pathname: string): boolean => /^\/edit\/[^/]+\/?$/.test(pathname);

export const PaidWithFormParticipateModal: FC<PaidWithFormParticipateModalProps> = ({
  onClose,
  competitionId,
  participationProjects = [],
  pendingProjects = [],
  rejectedProjects = [],
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const modal = useModal();
  const dispatch = useDispatch();
  const toasts = useToasts();
  const { activeTariff } = useSelector(selectorOrder);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (isEditProjectPath(location.pathname)) {
        onClose?.();
      }
    });

    return unlisten;
  }, [history, onClose]);
  
  const renderProjectCards = (
    items: Record<string, unknown>[],
    options?: { passCompetitionContext?: boolean; isCanNotBeDraft?: boolean },
  ) =>
    items.map((v, index) => {
      const project = v as Nullable<Partial<Project<'Local'>>>;
      const translatedProject = API.toLang(project, i18n.language) as Nullable<
        Partial<Project<'Local'>>
      >;
      return (
        <ProjectCardNew
          project={translatedProject as any}
          isModal
          isEditable
          withoutRepost
          withoutGoToProject={false}
          isRejected={ProjectDelegate.isRejectedProject(translatedProject as any)}
          key={translatedProject?.projectId}
          {...computeProject(translatedProject)}
          isSelectable={false}
          linkTarget="_blank"
          onProjectClick={(projectId, ev) => {
            if (!projectId || !ev) return;
            ev.preventDefault();
            window.open(CreateLinkTo.project(projectId), '_blank', 'noopener,noreferrer');
          }}
          {...(options?.passCompetitionContext && competitionId
            ? { currentCompetitionId: competitionId }
            : {})}
          {...(options?.isCanNotBeDraft ? { isCanNotBeDraft: true } : {})}
        />
      );
    });

  const navigateToLegalEntityProjectFirstFlow = () => {
    if (!competitionId) {
      toasts.error(t('competition.error_data'));
      return;
    }
    const qs = new URLSearchParams({
      openModal: 'send-project',
      competitionId,
      singleProjectSelection: '1',
      [QUERY_LEGAL_CONTEST_PROJECT_THEN_FORM]: '1',
    }).toString();
    onClose?.();
    modal.hide();
    history.push(`${CreateLinkTo.competition(competitionId)}?${qs}`);
  };

  const handlePayClick = async () => {
    if (isLoading) {
      return;
    }

    if (!competitionId) {
      toasts.error(t('competition.error_data'));
      return;
    }

    setIsLoading(true);
    try {
      dispatch(clearOrderFields());

      const competitionCardResponse = await API.request()
        .endpoint(Endpoints.competitions)
        .url('/api/Competition/GetCompetitionCard')
        .addQuery('competitionId', competitionId)
        .get()
        .send();

      if (!competitionCardResponse?.ok) {
        throw new Error('Failed to fetch competition card');
      }

      const competitionCard = await competitionCardResponse.json();
      const cardId = competitionCard?.cardId;
      if (!cardId) {
        throw new Error('Competition card id is missing');
      }

      const rawCardPrice = competitionCard?.priceInfo?.realProductPrice;
      const cardPrice = Number(rawCardPrice) || 2500;
      const cardName = Array.isArray(competitionCard?.name)
        ? competitionCard.name.find((n: CompetitionCardNameItem) => n?.lang?.toLowerCase() === 'ru')?.name || competitionCard.name[0]?.name
        : competitionCard?.name;

      dispatch(setImmediatePurchaseProps({
        productId: cardId,
        paymentEntityType: EntityPaymentTypes.product_card,
        flowType: FlowType.COPY_CARD,
        currencyType: CurrencyType.RealMoney,
        tariff: activeTariff,
        price: cardPrice,
        productInfo: {
          id: cardId,
          name: cardName || t('competition.paid_with_form_modal.content_title'),
          preview: competitionCard?.images || [],
          price: cardPrice,
          type: EntityPaymentTypes.product_card,
          typeCard: 'product',
        },
      }));

      onClose?.();
      modal.hide();
      modal.show('paymentManager', {
        containerStyles: {
          background: 'rgba(231,231,231,1)',
          backdropFilter: 'none',
        },
        flowType: FlowType.COPY_CARD,
        sharedProps: {
          competitionId,
        },
        cancel: () => {
          dispatch(clearOrderFields());
        },
      });
    } catch (error) {
      console.error('Ошибка при загрузке данных о конкурсе:', error);
      toasts.error(t('competition.error_data'));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Wrapper>
      <Title>{t('competition.paid_with_form_modal.title')}</Title>
      <Container>
        <ContentTitle>{t('competition.paid_with_form_modal.content_title')}</ContentTitle>
        <ContentDesc>{t('competition.paid_with_form_modal.content_desc')}</ContentDesc>
        <ContentDescSecondary>
          {t('competition.paid_with_form_modal.content_desc_secondary')}
        </ContentDescSecondary>
        <ActionsRow>
          <StyledActionButton
            variant={ButtonVariant.Hollow}
            onClick={navigateToLegalEntityProjectFirstFlow}
            disabled={isLoading}
          >
            {t('competition.paid_with_form_modal.pay_legal_entities')}
          </StyledActionButton>
          <StyledActionButton
            variant={ButtonVariant.Filled}
            onClick={handlePayClick}
            disabled={isLoading}
          >
            {t('competition.paid_with_form_modal.pay_online')}
          </StyledActionButton>
        </ActionsRow>
      </Container>
      {participationProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>Утвердженные проекты</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(participationProjects)}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
      {pendingProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>Проекты на модерации</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(pendingProjects)}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
      {rejectedProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>{t('competition.paid_with_form_modal.rejected_section_heading')}</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(rejectedProjects, { passCompetitionContext: true, isCanNotBeDraft: true })}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
    </Wrapper>
  );
};
import { API, CurrencyType, Endpoints, Nullable, Project } from '@brg/api';
import { ButtonVariant, useToasts } from '@brg/ui';
import { ProjectDelegate } from 'project-delegate';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ProjectCardNew from 'shared/components/projectCard/projectCardNew';
import { ProjectsGridNew } from 'shared/components/grids';
import { computeProject } from 'shared/helpers/types/computed/ComputedProject';
import { CreateLinkTo } from 'shared/constants/linkTo';
import { useModal } from '../../../core';
import { EntityPaymentTypes } from '../../features/QueuePaymenrt/constants/payment';
import { FlowType } from '../../features/QueuePaymenrt/constants/flowType';
import { clearOrderFields, selectorOrder, setImmediatePurchaseProps } from '../../features/QueuePaymenrt/slices/OrderSlice';
import { QUERY_LEGAL_CONTEST_PROJECT_THEN_FORM } from '_competition/const';
import {
  ActionsRow,
  Container,
  ContentDesc,
  ContentDescSecondary,
  ContentTitle,
  ProjectsSection,
  SectionHeading,
  StyledActionButton,
  Title,
  Wrapper,
} from './PaidWithFormParticipateModal.styles';


export type PaidWithFormParticipateModalProps = {
  onClose?: () => void;
  competitionId?: string;
  participationProjects?: Project[];
  pendingProjects?: Project[];
  rejectedProjects?: Project[];
};
interface CompetitionCardNameItem {
  lang?: string;
  name?: string;
}

const isEditProjectPath = (pathname: string): boolean => /^\/edit\/[^/]+\/?$/.test(pathname);

export const PaidWithFormParticipateModal: FC<PaidWithFormParticipateModalProps> = ({
  onClose,
  competitionId,
  participationProjects = [],
  pendingProjects = [],
  rejectedProjects = [],
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const modal = useModal();
  const dispatch = useDispatch();
  const toasts = useToasts();
  const { activeTariff } = useSelector(selectorOrder);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (isEditProjectPath(location.pathname)) {
        onClose?.();
      }
    });

    return unlisten;
  }, [history, onClose]);
  
  const renderProjectCards = (
    items: Record<string, unknown>[],
    options?: { passCompetitionContext?: boolean; isCanNotBeDraft?: boolean },
  ) =>
    items.map((v, index) => {
      const project = v as Nullable<Partial<Project<'Local'>>>;
      const translatedProject = API.toLang(project, i18n.language) as Nullable<
        Partial<Project<'Local'>>
      >;
      return (
        <ProjectCardNew
          project={translatedProject as any}
          isModal
          isEditable
          withoutRepost
          withoutGoToProject={false}
          isRejected={ProjectDelegate.isRejectedProject(translatedProject as any)}
          key={translatedProject?.projectId}
          {...computeProject(translatedProject)}
          isSelectable={false}
          linkTarget="_blank"
          onProjectClick={(projectId, ev) => {
            if (!projectId || !ev) return;
            ev.preventDefault();
            window.open(CreateLinkTo.project(projectId), '_blank', 'noopener,noreferrer');
          }}
          {...(options?.passCompetitionContext && competitionId
            ? { currentCompetitionId: competitionId }
            : {})}
          {...(options?.isCanNotBeDraft ? { isCanNotBeDraft: true } : {})}
        />
      );
    });

  const navigateToLegalEntityProjectFirstFlow = () => {
    if (!competitionId) {
      toasts.error(t('competition.error_data'));
      return;
    }
    const qs = new URLSearchParams({
      openModal: 'send-project',
      competitionId,
      singleProjectSelection: '1',
      [QUERY_LEGAL_CONTEST_PROJECT_THEN_FORM]: '1',
    }).toString();
    onClose?.();
    modal.hide();
    history.push(`${CreateLinkTo.competition(competitionId)}?${qs}`);
  };

  const handlePayClick = async () => {
    if (isLoading) {
      return;
    }

    if (!competitionId) {
      toasts.error(t('competition.error_data'));
      return;
    }

    setIsLoading(true);
    try {
      dispatch(clearOrderFields());

      const competitionCardResponse = await API.request()
        .endpoint(Endpoints.competitions)
        .url('/api/Competition/GetCompetitionCard')
        .addQuery('competitionId', competitionId)
        .get()
        .send();

      if (!competitionCardResponse?.ok) {
        throw new Error('Failed to fetch competition card');
      }

      const competitionCard = await competitionCardResponse.json();
      const cardId = competitionCard?.cardId;
      if (!cardId) {
        throw new Error('Competition card id is missing');
      }

      const rawCardPrice = competitionCard?.priceInfo?.realProductPrice;
      const cardPrice = Number(rawCardPrice) || 2500;
      const cardName = Array.isArray(competitionCard?.name)
        ? competitionCard.name.find((n: CompetitionCardNameItem) => n?.lang?.toLowerCase() === 'ru')?.name || competitionCard.name[0]?.name
        : competitionCard?.name;

      dispatch(setImmediatePurchaseProps({
        productId: cardId,
        paymentEntityType: EntityPaymentTypes.product_card,
        flowType: FlowType.COPY_CARD,
        currencyType: CurrencyType.RealMoney,
        tariff: activeTariff,
        price: cardPrice,
        productInfo: {
          id: cardId,
          name: cardName || t('competition.paid_with_form_modal.content_title'),
          preview: competitionCard?.images || [],
          price: cardPrice,
          type: EntityPaymentTypes.product_card,
          typeCard: 'product',
        },
      }));

      onClose?.();
      modal.hide();
      modal.show('paymentManager', {
        containerStyles: {
          background: 'rgba(231,231,231,1)',
          backdropFilter: 'none',
        },
        flowType: FlowType.COPY_CARD,
        sharedProps: {
          competitionId,
        },
        cancel: () => {
          dispatch(clearOrderFields());
        },
      });
    } catch (error) {
      console.error('Ошибка при загрузке данных о конкурсе:', error);
      toasts.error(t('competition.error_data'));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Wrapper>
      <Title>{t('competition.paid_with_form_modal.title')}</Title>
      <Container>
        <ContentTitle>{t('competition.paid_with_form_modal.content_title')}</ContentTitle>
        <ContentDesc>{t('competition.paid_with_form_modal.content_desc')}</ContentDesc>
        <ContentDescSecondary>
          {t('competition.paid_with_form_modal.content_desc_secondary')}
        </ContentDescSecondary>
        <ActionsRow>
          <StyledActionButton
            variant={ButtonVariant.Hollow}
            onClick={navigateToLegalEntityProjectFirstFlow}
            disabled={isLoading}
          >
            {t('competition.paid_with_form_modal.pay_legal_entities')}
          </StyledActionButton>
          <StyledActionButton
            variant={ButtonVariant.Filled}
            onClick={handlePayClick}
            disabled={isLoading}
          >
            {t('competition.paid_with_form_modal.pay_online')}
          </StyledActionButton>
        </ActionsRow>
      </Container>
      {participationProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>Утвердженные проекты</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(participationProjects)}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
      {pendingProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>Проекты на модерации</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(pendingProjects)}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
      {rejectedProjects.length > 0 && (
        <ProjectsSection>
          <SectionHeading>{t('competition.paid_with_form_modal.rejected_section_heading')}</SectionHeading>
          <ProjectsGridNew>
            {renderProjectCards(rejectedProjects, { passCompetitionContext: true, isCanNotBeDraft: true })}
          </ProjectsGridNew>
        </ProjectsSection>
      )}
    </Wrapper>
  );
};
